import { useSurfaceProps } from '../surfaces/util/SurfaceProvider'
import React from 'react'
import { GeckoAnnularRing, GeckoComponentPath } from 'types'

import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from './procedural/SurfaceLineFeature3D'

/**
 * Renders a 3D annular ring feature as a circular line on a component surface.
 *
 * The component creates a circular path based on the ring's inner radius and renders
 * it as a selectable line feature. The circle is centered within the component bounds
 * and the radius is converted to match the component's coordinate system units.
 *
 * @param model - The GeckoAnnularRing model containing the inner radius and other properties
 */
export const AnnularRingFeature3D: React.FC<{
  model: GeckoAnnularRing
}> = ({ model }) => {
  const { bounds, componentModel } = useSurfaceProps()

  const path = React.useMemo(() => {
    const centerX = (bounds.x.start.num + bounds.x.end.num) / 2
    const centerY = (bounds.y.start.num + bounds.y.end.num) / 2
    const r = model.innerRadius.convertTo(bounds.x.unit).num
    return GeckoComponentPath.CreateFromCircle(
      componentModel?.id ?? '',
      centerX,
      centerY,
      r,
      bounds.x.unit,
      bounds.y.unit,
      128
    )
  }, [bounds, componentModel?.id, model.innerRadius])

  return (
    <SurfaceLineFeature3D
      path={path}
      featureModel={model}
      lineSelectionType={SurfaceLineSelectionType.FeatureLine}
    />
  )
}
